import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { View, Text, Pressable, Alert, Platform, ActivityIndicator } from 'react-native';
import { useRoute, useNavigation, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { YStack, XStack, Separator } from 'tamagui';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { Card } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { SHOW_CALL_MODAL } from '@reduxLocal/actions/types';
import apis from '@api/api';

import FlowHierarchy, { FlowHierarchyHandle, StageItem } from './Components/EnhancedFlowHierarchy';
import StageEditor from './Components/StageEditor';

type FlowEditorRouteProp = RouteProp<any, 'FlowEditor'>;
type FlowEditorNavigationProp = StackNavigationProp<any, 'FlowEditor'>;

interface FlowStage {
  _id: string;
  title: string;
  description: string;
  type: 'greeting' | 'introduction' | 'data_collection' | 'setup' | 'training' | 'completion' | 'diagnosis' | 'testing' | 'resolution' | 'action' | 'decision' | 'notification' | 'approval' | 'review' | 'verification' | 'custom' | 'primary';
  order: number;
  x?: number;
  y?: number;
  config?: {
    estimatedDuration?: number;
    required?: boolean;
    skippable?: boolean;
    repeatable?: boolean;
    maxRepetitions?: number;
  };
  dataCollection?: {
    fields?: Array<{
      name: string;
      type: string; // Allow any string to match StageItem type
      required?: boolean;
      validation?: string;
      options?: string[];
      helpText?: string;
      example?: string;
      placeholder?: string;
      label?: string;
    }>;
    form?: string;
  };
  stageActions?: Array<{
    actionType: string; // Allow any string to match StageItem type
    config?: any;
    triggerOn?: string; // Allow any string to match StageItem type
    calendarConfig?: {
      calendarId?: string;
      duration?: number;
      buffer?: number;
      autoConfirm?: boolean;
    };
  }>;
  voiceConfig?: {
    aiPrompt?: string;
    expectedPhrases?: string[];
    navigationKeywords?: string[];
    provideContext?: boolean;
    ragEnhancement?: string;
  };
  conditions?: {
    showIf?: any;
    skipIf?: any;
    nextStageRules?: Array<{
      condition: any;
      nextStageIndex: number;
    }>;
  };
  resources?: {
    documentation?: string[];
    videos?: string[];
    images?: string[];
    externalLinks?: Array<{
      title: string;
      url: string;
    }>;
  };
  verificationConfig?: {
    dataSchemaId?: string;
    fieldsToVerify?: string[];
    matchThreshold?: number;
    availableFields?: Array<{
      name: string;
      type: string;
      label: string;
    }>;
    onSuccess?: {
      actionType: string;
      config?: any;
    };
    onFailure?: {
      actionType: string;
      config?: any;
    };
  };
  metadata?: any;
}

interface FlowData {
  _id: string;
  title: string;
  description: string;
  flowType: 'onboarding' | 'support' | 'sales' | 'training' | 'approval' | 'workflow' | 'troubleshooting' | 'general';
  category?: string;
  tags?: string[];
  actions: FlowStage[];
  config?: {
    allowSkipping?: boolean;
    allowBackNavigation?: boolean;
    autoAdvance?: boolean;
    showProgress?: boolean;
    estimatedDuration?: number;
    maxDuration?: number;
    requireAllStages?: boolean;
  };
  voiceCallConfig?: {
    enabled?: boolean;
    welcomeMessage?: string;
    completionMessage?: string;
    aiInstructions?: string;
    useRAG?: boolean;
    ragConfig?: {
      maxDocuments?: number;
      relevanceThreshold?: number;
      includeFlowContext?: boolean;
    };
    voiceCommands?: {
      nextStage?: string[];
      previousStage?: string[];
      repeatStage?: string[];
      skipStage?: string[];
      getHelp?: string[];
    };
  };
  analytics?: {
    totalExecutions?: number;
    completedExecutions?: number;
    averageCompletionTime?: number;
    averageStageTime?: number[];
    dropOffStages?: any;
    completionRate?: number;
    lastExecuted?: Date;
  };
  status: 'draft' | 'active' | 'inactive' | 'archived' | 'testing';
  version: number;
  shared: boolean;
  permission: boolean;
  userId: string;
  companyId?: string;
  createdBy: any;
  createdAt?: Date;
  updatedAt?: Date;
}

const FlowEditor = () => {
  const theme = themeSelector();
  const route = useRoute<FlowEditorRouteProp>();
  const navigation = useNavigation<FlowEditorNavigationProp>();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const flowHierarchyRef = useRef<FlowHierarchyHandle>(null);

  // Stage Editor state
  const [isEditorVisible, setIsEditorVisible] = useState(false);
  const [editingStage, setEditingStage] = useState<StageItem | null>(null);

  // Header collapse state
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);

  // Get flow data from route params
  const params = route.params as any;
  const { flowId, mode = 'edit' } = params || {};
  const isEditMode = mode === 'edit';

  // Fetch call flows data with automatic refresh
  const { data: callFlowsData, isLoading: isLoadingFlows, error: flowsError, refetch: refetchFlows } = useQuery({
    queryKey: ['callFlows'],
    queryFn: async () => {
      try {
        const response = await apis.getCallFlows(''); // Empty string for companyId (backend handles it)
        return response?.flows || response || [];
      } catch (error) {
        console.error('Error fetching call flows:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!flowId && flowId !== 'new-flow', // Only fetch if we have a flowId
  });

  // Get specific flow data from the flows list
  const flowData = callFlowsData?.find((flow: any) => flow._id === flowId);

  // Set loading/error states
  const isLoading = isLoadingFlows && !!flowId && flowId !== 'new-flow';
  const isError = (!!flowsError || (!isLoadingFlows && !flowData && callFlowsData?.length >= 0)) && flowId !== 'new-flow';
  const error = isError
    ? {
      message: flowsError?.message
        || (!flowData && callFlowsData
          ? `Flow not found (ID: ${flowId}). It may have been deleted.`
          : 'Failed to load flow data')
    }
    : null;

  // Initialize flow data from query or create empty structure
  const initialFlowData: FlowData = useMemo(() => {
    if (flowData) {
      return {
        _id: flowData._id || flowId || 'new-flow',
        title: flowData.title || 'New Flow',
        description: flowData.description || '',
        flowType: flowData.flowType || 'general',
        category: flowData.category || 'General',
        tags: flowData.tags || [],
        actions: flowData.actions || [],
        config: flowData.config || {
          allowSkipping: false,
          allowBackNavigation: true,
          autoAdvance: false,
          showProgress: true,
          requireAllStages: true
        },
        voiceCallConfig: flowData.voiceCallConfig || {
          enabled: true,
          useRAG: true,
          ragConfig: {
            maxDocuments: 5,
            relevanceThreshold: 0.7,
            includeFlowContext: true
          }
        },
        analytics: flowData.analytics || {
          totalExecutions: 0,
          completedExecutions: 0,
          completionRate: 0
        },
        status: flowData.status || 'draft',
        version: flowData.version || 1,
        shared: flowData.shared !== undefined ? flowData.shared : true,
        permission: flowData.permission !== undefined ? flowData.permission : true,
        userId: flowData.userId || '',
        companyId: flowData.companyId,
        createdBy: flowData.createdBy,
        createdAt: flowData?.createdAt,
        updatedAt: flowData.updatedAt
      };
    }
    // Return empty structure for new flow
    return {
      _id: flowId || 'new-flow',
      title: 'New Flow',
      description: '',
      flowType: 'general',
      category: 'General',
      tags: [],
      actions: [],
      config: {
        allowSkipping: false,
        allowBackNavigation: true,
        autoAdvance: false,
        showProgress: true,
        requireAllStages: true
      },
      voiceCallConfig: {
        enabled: true,
        useRAG: true
      },
      analytics: {
        totalExecutions: 0,
        completedExecutions: 0,
        completionRate: 0
      },
      status: 'draft',
      version: 1,
      shared: false,
      permission: true,
      userId: '',
      companyId: '',
      createdBy: null
    };
  }, [flowData, flowId]);

  // State for managing stages - ensure it's always a valid array
  const [stages, setStages] = useState<FlowStage[]>(() => {
    if (!initialFlowData || !Array.isArray(initialFlowData.actions)) {
      return [];
    }
    return initialFlowData.actions.filter(s => s != null && s._id != null);
  });
  const [flowDataState, setFlowData] = useState<FlowData>(() => {
    // Ensure flowDataState is always valid
    if (!initialFlowData) {
      return {
        _id: 'temp-flow',
        title: 'Loading...',
        description: '',
        flowType: 'general',
        category: 'General',
        tags: [],
        actions: [],
        config: { allowSkipping: false, allowBackNavigation: true, autoAdvance: false, showProgress: true, requireAllStages: true },
        voiceCallConfig: { enabled: true, useRAG: true },
        analytics: { totalExecutions: 0, completedExecutions: 0, completionRate: 0 },
        status: 'draft',
        version: 1,
        shared: false,
        permission: true,
        userId: '',
        companyId: '',
        createdBy: null
      };
    }
    return initialFlowData;
  });
  const [selectedStage, setSelectedStage] = useState<string | null>(null);

  // Stage Editor handlers (after stages state declaration)
  const handleEditStage = useCallback((stage: StageItem) => {
    setEditingStage(stage);
    setIsEditorVisible(true);
  }, []);

  const handleCloseEditor = useCallback(() => {
    setIsEditorVisible(false);
    setEditingStage(null);
  }, []);

  // Track if we've already initialized to prevent unnecessary updates
  const hasInitialized = useRef(false);
  const lastUpdatedAt = useRef<Date | undefined>(undefined);

  // Update local state when query data changes (but not when we just saved)
  React.useEffect(() => {
    if (!flowData) return;

    // Skip if this is just a cache update from our own save (same updatedAt)
    if (hasInitialized.current &&
      lastUpdatedAt.current &&
      flowData.updatedAt &&
      new Date(flowData.updatedAt).getTime() === new Date(lastUpdatedAt.current).getTime()) {
      return;
    }

    const transformedFlow: FlowData = {
      _id: flowData._id || flowId || 'new-flow',
      title: flowData.title || 'New Flow',
      description: flowData.description || '',
      flowType: flowData.flowType || 'general',
      category: flowData.category || 'General',
      tags: flowData.tags || [],
      actions: flowData.actions || [],
      config: flowData.config || {
        allowSkipping: false,
        allowBackNavigation: true,
        autoAdvance: false,
        showProgress: true,
        requireAllStages: true
      },
      voiceCallConfig: flowData.voiceCallConfig || {
        enabled: true,
        useRAG: true
      },
      analytics: flowData.analytics || {
        totalExecutions: 0,
        completedExecutions: 0,
        completionRate: 0
      },
      status: flowData.status || 'draft',
      version: flowData.version || 1,
      shared: flowData.shared !== undefined ? flowData.shared : true,
      permission: flowData.permission !== undefined ? flowData.permission : true,
      userId: flowData.userId || '',
      companyId: flowData.companyId,
      createdBy: flowData.createdBy,
      createdAt: flowData.createdAt,
      updatedAt: flowData.updatedAt
    };

    setFlowData(transformedFlow);
    // Ensure stages is always a valid array with valid items
    const validStages = Array.isArray(transformedFlow.actions)
      ? transformedFlow.actions.filter(s => s != null && s._id != null)
      : [];
    setStages(validStages);
    lastUpdatedAt.current = flowData.updatedAt;
    hasInitialized.current = true;
  }, [flowData, flowId]);

  // Stage Editor handlers
  const handleSaveStage = useCallback(async (stageId: string, updatedStage: Partial<StageItem>) => {
    try {
      // CRITICAL: If stageId is undefined, we need to find the stage by other means
      if (!stageId || stageId === 'undefined') {
        Alert.alert('Error', 'Stage ID is missing. Cannot save changes.');
        return;
      }

      // Check if this is a legacy stage ID (from old flows without stage IDs)
      const isLegacyId = typeof stageId === 'string' && stageId.startsWith('legacy-stage-');
      const legacyOrder = isLegacyId ? parseInt(stageId.replace('legacy-stage-', '')) : null;

      // Update the stages state
      const updatedStages = stages.map(stage => {
        // Safety check for null/undefined stage
        if (!stage) return stage;

        // Match by _id OR by order for legacy stages
        const isMatch = isLegacyId
          ? stage.order === legacyOrder
          : stage._id === stageId;

        if (isMatch) {
          // Build the updated stage object - preserve x/y coordinates!
          const newStage = {
            ...stage,
            ...updatedStage,
            // Preserve position coordinates from the original stage
            x: stage.x,
            y: stage.y,
            // Deep merge config
            config: updatedStage.config !== undefined
              ? { ...stage.config, ...updatedStage.config }
              : stage.config,
            // Deep merge voiceConfig
            voiceConfig: updatedStage.voiceConfig !== undefined
              ? { ...stage.voiceConfig, ...updatedStage.voiceConfig }
              : stage.voiceConfig,
            // Deep merge verificationConfig
            verificationConfig: updatedStage.verificationConfig !== undefined
              ? { ...stage.verificationConfig, ...updatedStage.verificationConfig }
              : stage.verificationConfig,
            // Deep merge dataCollection
            dataCollection: updatedStage.dataCollection !== undefined
              ? { ...stage.dataCollection, ...updatedStage.dataCollection }
              : stage.dataCollection,
            // Replace arrays only if explicitly provided (not undefined)
            stageActions: updatedStage.stageActions !== undefined
              ? updatedStage.stageActions
              : stage.stageActions,
            conditions: updatedStage.conditions !== undefined
              ? updatedStage.conditions
              : stage.conditions,
            resources: updatedStage.resources !== undefined
              ? updatedStage.resources
              : stage.resources,
            // Deep merge metadata
            metadata: updatedStage.metadata !== undefined
              ? { ...stage.metadata, ...updatedStage.metadata }
              : stage.metadata
          };

          return newStage;
        }
        return stage;
      });

      setStages(updatedStages);

      // Also update the flowDataState to keep it in sync
      const updatedFlowData = {
        ...flowDataState,
        actions: updatedStages
      };
      setFlowData(updatedFlowData);

      // Auto-save to backend if flowId exists and is not new
      if (flowId && flowId !== 'new-flow') {
        // Sanitize stages for backend - ensure validation is string, not object
        const sanitizedStages = updatedStages.filter(s => s != null).map(stage => {
          const sanitizedStage = { ...stage };

          // Sanitize dataCollection fields
          if (sanitizedStage?.dataCollection?.fields) {
            sanitizedStage.dataCollection.fields = sanitizedStage.dataCollection.fields.filter(f => f != null).map(field => ({
              ...field,
              // Convert validation to string if it's an object
              validation: typeof field?.validation === 'object'
                ? JSON.stringify(field.validation)
                : field?.validation
            }));
          }

          return sanitizedStage;
        });

        const flowPayload = {
          title: flowDataState.title,
          description: flowDataState.description,
          flowType: flowDataState.flowType,
          category: flowDataState.category,
          tags: flowDataState.tags,
          actions: sanitizedStages,
          config: flowDataState.config,
          voiceCallConfig: flowDataState.voiceCallConfig,
          shared: flowDataState.shared,
          permission: flowDataState.permission,
          status: flowDataState.status
        };

        try {
          await apis.updateFlow(flowId, flowPayload);

          // Don't update query cache - it will cause unnecessary re-renders
          // The local state is already updated and that's what the UI uses
          // Query cache will be updated when user navigates away and back
        } catch (apiError) {
          console.error('Error saving to backend:', apiError);
          // Still keep the local changes even if backend save fails
        }
      }
    } catch (error) {
      console.error('Error saving stage:', error);
      Alert.alert('Error', 'Failed to save stage changes. Please try again.');
    }
  }, [stages, flowId, flowDataState]);

  // Handle stage position changes
  const handleStagePositionChange = useCallback((stageId: string, position: { x: number, y: number }) => {
  }, []);

  // Handle stage connections
  const handleStageConnectionChange = useCallback((sourceId: string, targetId: string, type: string) => {
    console.log('🔗 Stage connection created:', sourceId, '->', targetId, type);

    const sourceStage = stages.find(s => s?._id === sourceId);
    const targetStage = stages.find(s => s?._id === targetId);

    if (!sourceStage || !targetStage) {
      console.error('Stage not found for connection:', { sourceId, targetId });
      return;
    }

    Alert.alert(
      'Stage Connection',
      `Stage "${sourceStage.title}" now leads to "${targetStage.title}"`,
      [{ text: 'OK' }]
    );
  }, [stages]);

  // Handle adding new stage
  const handleAddStage = useCallback(() => {
    // Find the maximum order value from existing main stages (not sub-stages)
    const mainStages = stages.filter(s => s != null && !s?.metadata?.isSubStage && !s?._id?.startsWith('substage_'));
    const maxOrder = mainStages.length > 0
      ? Math.max(...mainStages.map(s => s?.order || 0))
      : -1;

    const newOrder = maxOrder + 1;

    const newStage: FlowStage = {
      _id: `stage_${Date.now()}`,
      title: 'New Stage',
      description: 'Click to edit this stage',
      type: 'custom',
      order: newOrder,
      config: {
        estimatedDuration: 5,
        required: true,
        skippable: false,
        repeatable: false,
        maxRepetitions: 1
      },
      voiceConfig: {
        provideContext: true,
        aiPrompt: 'Guide the user through this stage',
        expectedPhrases: [],
        navigationKeywords: []
      }
    };

    const updatedStages = [...stages, newStage];
    setStages(updatedStages);

    // Update flowDataState to keep it in sync
    setFlowData(prev => ({
      ...prev,
      actions: updatedStages
    }));

    console.log(`➕ Added new stage with order ${newOrder}:`, newStage.title);
  }, [stages]);

  // Handle adding sub-stage (similar to handleAddSubModule in AcademyCourseEditor)
  const handleAddSubStage = useCallback((parentStageId: string) => {
    console.log('➕ Adding sub-stage to parent:', parentStageId);

    const parentStage = stages.find(s => s?._id === parentStageId);

    if (!parentStage) {
      console.error('Parent stage not found:', parentStageId);
      Alert.alert('Error', 'Parent stage not found. Cannot add sub-stage.');
      return;
    }

    // Count existing sub-stages for this parent to determine sub-order
    const siblingSubStages = stages.filter(
      s => s?.metadata?.parentStageId === parentStageId && s?.metadata?.isSubStage
    );
    const subOrder = siblingSubStages.length;

    const newSubStage: FlowStage = {
      _id: `substage_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: 'New Sub-Stage',
      description: 'Click to edit this sub-stage',
      type: 'custom',
      order: parentStage ? parentStage.order : 0, // Same order as parent for positioning reference
      config: {
        estimatedDuration: 3,
        required: false,
        skippable: true,
        repeatable: false,
        maxRepetitions: 1
      },
      voiceConfig: {
        provideContext: true,
        aiPrompt: `This is a sub-stage of ${parentStage?.title}. Provide additional details or clarification.`,
        expectedPhrases: [],
        navigationKeywords: []
      },
      metadata: {
        parentStageId: parentStageId, // Mark as sub-stage
        isSubStage: true,
        subOrder: subOrder // Order relative to siblings under same parent
      }
    };

    const updatedStages = [...stages, newSubStage];
    setStages(updatedStages);

    // Update flowDataState to keep it in sync
    setFlowData(prev => ({
      ...prev,
      actions: updatedStages
    }));

    // Note: Position will be calculated by FlowHierarchy based on parent position
    console.log(`✅ Sub-stage added (sub-order ${subOrder}):`, newSubStage._id);
  }, [stages]);

  // Handle deleting stage
  const handleDeleteStage = useCallback((stageId: string) => {
    if (!stageId) {
      console.error('Cannot delete stage: stageId is undefined');
      return;
    }

    Alert.alert(
      'Delete Stage',
      'Are you sure you want to delete this stage?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            const updatedStages = stages.filter(s => s?._id !== stageId);
            setStages(updatedStages);

            // Update flowDataState to keep it in sync
            setFlowData(prev => ({
              ...prev,
              actions: updatedStages
            }));

            console.log('🗑️ Stage deleted:', stageId);
          }
        }
      ]
    );
  }, [stages]);

  // Handle saving flow
  const handleSaveFlow = useCallback(async () => {
    try {
      // Filter out null/undefined stages and sort by order
      const validStages = stages.filter(s => s != null);
      const sortedStages = [...validStages].sort((a, b) => (a?.order || 0) - (b?.order || 0));

      // Sanitize stages for backend - ensure validation is string, not object
      const sanitizedStages = sortedStages.map(stage => {
        const sanitizedStage = { ...stage };

        // Sanitize dataCollection fields
        if (sanitizedStage?.dataCollection?.fields) {
          sanitizedStage.dataCollection.fields = sanitizedStage.dataCollection.fields.filter(f => f != null).map(field => ({
            ...field,
            // Convert validation to string if it's an object
            validation: typeof field?.validation === 'object'
              ? JSON.stringify(field.validation)
              : field?.validation
          }));
        }

        return sanitizedStage;
      });

      const flowPayload = {
        title: flowDataState.title,
        description: flowDataState.description,
        flowType: flowDataState.flowType,
        category: flowDataState.category,
        tags: flowDataState.tags,
        actions: sanitizedStages,
        config: flowDataState.config,
        voiceCallConfig: flowDataState.voiceCallConfig,
        shared: flowDataState.shared,
        permission: flowDataState.permission,
        status: flowDataState.status
      };

      if (flowId && flowId !== 'new-flow') {
        // Update existing flow
        const response = await apis.updateFlow(flowId, flowPayload);

        // Update both the individual flow query and the callFlows list query
        queryClient.setQueryData(['flow', flowId], (oldData: any) => ({
          ...oldData,
          ...flowPayload
        }));

        // Update the flow in the callFlows list cache
        queryClient.setQueryData(['callFlows'], (oldData: any) => {
          if (!oldData) return oldData;
          return oldData.map((flow: any) =>
            flow._id === flowId ? { ...flow, ...flowPayload } : flow
          );
        });

        Alert.alert('Success', 'Flow updated successfully!');
        console.log('💾 Flow updated:', response);
      } else {
        // Create new flow
        const response = await apis.createFlow(flowPayload);

        if (response.success) {
          // Invalidate callFlows query to refetch with new flow
          queryClient.invalidateQueries({ queryKey: ['callFlows'] });

          Alert.alert('Success', 'Flow created successfully!');
          console.log('💾 Flow created:', response);

          // Navigate back after successful creation
          setTimeout(() => {
            navigation.goBack();
          }, 1000);
        } else {
          throw new Error('Failed to create flow');
        }
      }
    } catch (error) {
      console.error('Error saving flow:', error);
      Alert.alert('Error', 'Failed to save flow. Please try again.');
    }
  }, [stages, flowDataState, flowId, navigation, queryClient]);

  // Handle testing flow via call modal with WebRTC/Whisper integration
  const handleTestFlow = useCallback(() => {
    // Validate that we have valid data before testing
    if (!flowDataState || !flowId) {
      Alert.alert('Error', 'Flow data is not ready. Please wait or refresh.');
      return;
    }

    console.log('🎙️ Starting flow test with WebRTC/Whisper integration:', {
      flowId,
      title: flowDataState?.title,
      stages: stages?.length || 0,
      voiceEnabled: flowDataState?.voiceCallConfig?.enabled
    });

    dispatch({
      type: SHOW_CALL_MODAL,
      status: "testing",
      payload: {
        handle: flowDataState?.title || 'Test Flow',
        callUUID: `flow-test-${flowId}-${Date.now()}`,
        avatar: flowDataState?.createdBy?.profile_information?.avatar?.img,
        flowId: flowId || 'test-flow',
        flowData: {
          ...flowDataState,
          actions: Array.isArray(stages) ? stages.filter(s => s != null && s._id != null) : [], // Use live stages data
          // WebRTC/Whisper configuration
          webrtc: {
            enabled: true,
            whisperUrl: process.env.WHISPER_SERVICE_URL || 'http://localhost:11438',
            transcriptionEnabled: true,
            useWebSocket: true
          },
          // Voice call configuration for AI
          voiceCallConfig: {
            ...flowDataState.voiceCallConfig,
            welcomeMessage: flowDataState.voiceCallConfig?.welcomeMessage ||
              `Welcome! Let's test the ${flowDataState.title} flow. I'll guide you through each stage.`,
            completionMessage: flowDataState.voiceCallConfig?.completionMessage ||
              'Great! You\'ve completed the flow test. Thank you!',
            aiInstructions: flowDataState.voiceCallConfig?.aiInstructions ||
              'Guide the user through each stage of the flow. Be helpful and clear.',
            useRAG: flowDataState.voiceCallConfig?.useRAG !== false,
            ragConfig: flowDataState.voiceCallConfig?.ragConfig || {
              maxDocuments: 5,
              relevanceThreshold: 0.7,
              includeFlowContext: true
            }
          },
          // Testing mode flags
          testMode: true,
          testStartTime: new Date().toISOString()
        }
      }
    });

    // Log for debugging WebRTC/Whisper integration
    console.log('📞 Call modal dispatched with Whisper integration');
  }, [dispatch, flowId, flowDataState, stages]);

  // Handle publishing flow
  const handlePublishFlow = useCallback(async () => {
    if (!flowId || flowId === 'new-flow') {
      Alert.alert('Error', 'Please save the flow first before publishing.');
      return;
    }

    Alert.alert(
      'Publish Flow',
      'Are you sure you want to publish this flow? It will be activated for use.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Publish',
          onPress: async () => {
            try {
              await apis.updateFlow(flowId, { status: 'active' });

              // Update query cache
              queryClient.setQueryData(['flow', flowId], (oldData: any) => ({
                ...oldData,
                status: 'active'
              }));

              queryClient.setQueryData(['callFlows'], (oldData: any) => {
                if (!oldData) return oldData;
                return oldData.map((flow: any) =>
                  flow._id === flowId ? { ...flow, status: 'active' } : flow
                );
              });

              Alert.alert('Success', 'Flow published successfully!');
              setFlowData(prev => ({ ...prev, status: 'active' }));
            } catch (error) {
              console.error('Error publishing flow:', error);
              Alert.alert('Error', 'Failed to publish flow. Please try again.');
            }
          }
        }
      ]
    );
  }, [flowId, queryClient]);

  // Get flow type badge color
  const getFlowTypeBadgeColors = useCallback((flowType: string) => {
    const colors = {
      onboarding: { light: '#dbeafe', dark: '#1e3a8a', textLight: '#1e40af', textDark: '#93c5fd' },
      support: { light: '#fef3c7', dark: '#78350f', textLight: '#d97706', textDark: '#fbbf24' },
      sales: { light: '#dcfce7', dark: '#14532d', textLight: '#16a34a', textDark: '#86efac' },
      training: { light: '#e0e7ff', dark: '#312e81', textLight: '#4f46e5', textDark: '#a5b4fc' },
      approval: { light: '#fce7f3', dark: '#831843', textLight: '#be185d', textDark: '#f9a8d4' },
      workflow: { light: '#e0f2fe', dark: '#0c4a6e', textLight: '#0284c7', textDark: '#7dd3fc' },
      troubleshooting: { light: '#fed7aa', dark: '#7c2d12', textLight: '#ea580c', textDark: '#fdba74' },
      general: { light: '#f1f5f9', dark: '#1e293b', textLight: '#475569', textDark: '#cbd5e1' }
    };
    return colors[flowType] || colors.general;
  }, []);

  const flowTypeBadgeColors = getFlowTypeBadgeColors(flowDataState.flowType);

  // Calculate total estimated duration from live stages
  const totalEstimatedDuration = useMemo(() => {
    return stages.reduce((total, stage) => {
      if (!stage) return total;
      return total + (stage?.config?.estimatedDuration || 0);
    }, 0);
  }, [stages]);

  // Calculate total actions from live stages
  const totalStageActions = useMemo(() => {
    return stages.reduce((total, stage) => {
      if (!stage) return total;
      return total + (stage?.stageActions?.length || 0);
    }, 0);
  }, [stages]);

  // Memoize stage transformation with positions
  const transformedStages = useMemo(() => {
    // Safety check: ensure stages is an array
    if (!Array.isArray(stages)) {
      console.warn('Stages is not an array:', stages);
      return [];
    }

    // Filter out any null/undefined stages
    const validStages = stages.filter(stage => {
      if (stage == null) {
        console.warn('Found null/undefined stage');
        return false;
      }
      if (stage._id == null) {
        console.warn('Found stage without _id:', stage);
        return false;
      }
      return true;
    });

    if (validStages.length === 0) {
      console.log('No valid stages to transform');
      return [];
    }

    return validStages.map((stage, index) => {
      // Calculate horizontal position based on order value
      const componentWidth = 280;
      const componentHeight = 180;

      // Horizontal spacing between stages
      const horizontalSpacing = componentWidth + 150; // Space between stages horizontally

      // Calculate base flow component position (leftmost)
      // For horizontal flow, start from left side of viewport with margin
      const viewportWidth = 20000;
      const viewportHeight = 20000;
      const baseX = 200; // Left margin
      const baseY = (viewportHeight / 2) - (componentHeight / 2); // Vertically centered at 10000

      // Position stages horizontally based on their order value
      // Sub-stages will be positioned below their parent
      let stageX: number;
      let stageY: number;

      // Check if this stage has a parent (is a sub-stage)
      if (stage?.metadata?.parentStageId) {
        const parentStage = validStages.find(s => s?._id === stage.metadata?.parentStageId);
        if (parentStage) {
          // Position sub-stage below parent with horizontal offset based on sibling index
          const siblingsBefore = validStages.slice(0, index).filter(
            s => s?.metadata?.parentStageId === stage.metadata?.parentStageId
          ).length;

          // Parent's horizontal position (with safety for order)
          const parentOrder = parentStage.order != null ? parentStage.order : 0;
          const parentX = baseX + ((parentOrder + 1) * horizontalSpacing);
          const parentY = baseY;

          // Sub-stage positioned below parent
          stageX = parentX + (siblingsBefore * (horizontalSpacing * 0.5)); // Slight offset for siblings
          stageY = parentY + componentHeight + 100; // Below parent with gap
        } else {
          // Fallback if parent not found
          const stageOrder = stage.order != null ? stage.order : 0;
          stageX = baseX + ((stageOrder + 1) * horizontalSpacing);
          stageY = baseY;
        }
      } else {
        // Main stages positioned horizontally based on order
        // order 0 -> first position to the right of base
        // order 1 -> second position, etc.
        const stageOrder = stage.order != null ? stage.order : 0;
        stageX = baseX + ((stageOrder + 1) * horizontalSpacing);
        stageY = baseY;
      }

      return {
        ...stage, // Preserve all original properties including _id
        x: stageX,
        y: stageY,
        // For legacy flows without stage IDs, use a fallback based on order
        _id: stage._id || `legacy-stage-${stage.order}`
      };
    });
  }, [stages]);

  // Loading state
  if (isLoading) {
    return (
      <DefaultScreen
        scrollDisabled
        noMargin
        containerStyle={{ marginTop: 0, paddingTop: 0, paddingBottom: 0 }}
        style={{ flex: 1, paddingBottom: 0, paddingTop: 0 }}
      >
        <View style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20
        }}>
          <ActivityIndicator size="large" color={theme ? "#3b82f6" : "#60a5fa"} />
          <Text style={{
            marginTop: 16,
            fontSize: 16,
            color: theme ? "#64748b" : "#94a3b8"
          }}>
            Loading flow data...
          </Text>
        </View>
      </DefaultScreen>
    );
  }

  // Error state
  if (isError) {
    return (
      <DefaultScreen
        scrollDisabled
        noMargin
        containerStyle={{ marginTop: 0, paddingTop: 0, paddingBottom: 0 }}
        style={{ flex: 1, paddingBottom: 0, paddingTop: 0 }}
      >
        <View style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20
        }}>
          <MaterialCommunityIcons
            name="alert-circle"
            size={48}
            color="#ef4444"
          />
          <Text style={{
            marginTop: 16,
            fontSize: 16,
            color: "#ef4444",
            textAlign: 'center'
          }}>
            {error?.message || 'Failed to load flow data. Please try again.'}
          </Text>
          <XStack gap={12} marginTop={20}>
            <Pressable
              onPress={() => refetchFlows()}
              style={{
                paddingHorizontal: 24,
                paddingVertical: 12,
                borderRadius: 8,
                backgroundColor: theme ? "#3b82f6" : "#1e40af"
              }}
            >
              <Text style={{ color: 'white', fontWeight: '600' }}>Retry</Text>
            </Pressable>
            <Pressable
              onPress={() => navigation.goBack()}
              style={{
                paddingHorizontal: 24,
                paddingVertical: 12,
                borderRadius: 8,
                backgroundColor: theme ? "#f1f5f9" : "#334155"
              }}
            >
              <Text style={{ color: theme ? "#64748b" : "#94a3b8", fontWeight: '600' }}>Go Back</Text>
            </Pressable>
          </XStack>
        </View>
      </DefaultScreen>
    );
  }

  return (
    <DefaultScreen
      scrollDisabled
      noMargin
      containerStyle={{ marginTop: 0, paddingTop: 0, paddingBottom: 0 }}
      style={{ flex: 1, paddingBottom: 0, paddingTop: 0 }}
    >
      {/* Header */}
      <Card
        style={{
          margin: 20,
          marginBottom: 10,
          borderRadius: 16,
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderWidth: 1,
          borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: theme ? 0.1 : 0.3,
          shadowRadius: 12,
          elevation: 6,
        }}
      >
        <YStack padding={20}>
          {/* Collapsed Header - Minimal View */}
          {isHeaderCollapsed ? (
            <XStack alignItems="center" justifyContent="space-between">
              <XStack alignItems="center" gap={12} flex={1}>
                <Text style={{
                  fontSize: 18,
                  fontWeight: '700',
                  color: theme ? "#1e293b" : "#f1f5f9",
                }}>
                  {flowDataState.title}
                </Text>

                {/* Quick Stats in Collapsed Mode */}
                <XStack alignItems="center" gap={12}>
                  <XStack alignItems="center" gap={4}>
                    <MaterialCommunityIcons
                      name="stairs"
                      size={14}
                      color={theme ? "#64748b" : "#94a3b8"}
                    />
                    <Text style={{
                      fontSize: 11,
                      color: theme ? "#64748b" : "#94a3b8"
                    }}>
                      {stages.length}
                    </Text>
                  </XStack>

                  <XStack alignItems="center" gap={4}>
                    <MaterialIcons
                      name="access-time"
                      size={14}
                      color={theme ? "#64748b" : "#94a3b8"}
                    />
                    <Text style={{
                      fontSize: 11,
                      color: theme ? "#64748b" : "#94a3b8"
                    }}>
                      {totalEstimatedDuration}m
                    </Text>
                  </XStack>
                </XStack>

                {/* Mode Badge */}
                <View style={{
                  backgroundColor: isEditMode
                    ? (theme ? "#ddd6fe" : "#312e81")
                    : (theme ? "#dbeafe" : "#1e40af"),
                  paddingHorizontal: 10,
                  paddingVertical: 4,
                  borderRadius: 16,
                }}>
                  <Text style={{
                    fontSize: 10,
                    fontWeight: '600',
                    color: isEditMode
                      ? (theme ? "#7c3aed" : "#a78bfa")
                      : (theme ? "#1e40af" : "#93c5fd")
                  }}>
                    {isEditMode ? 'EDIT' : 'VIEW'}
                  </Text>
                </View>
              </XStack>

              {/* Expand Button */}
              <Pressable
                onPress={() => setIsHeaderCollapsed(false)}
                style={{
                  padding: 8,
                  borderRadius: 8,
                  backgroundColor: theme ? "#f1f5f9" : "#334155",
                }}
              >
                <MaterialCommunityIcons
                  name="chevron-down"
                  size={20}
                  color={theme ? "#64748b" : "#94a3b8"}
                />
              </Pressable>
            </XStack>
          ) : (
            <>
              {/* Expanded Header - Full View */}
              <XStack alignItems="center" justifyContent="space-between" marginBottom={16}>
                <YStack flex={1}>
                  <XStack alignItems="center" gap={12} marginBottom={8}>
                    {/* Flow Type Badge */}
                    <View style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 4,
                      paddingHorizontal: 10,
                      paddingVertical: 4,
                      backgroundColor: theme ? flowTypeBadgeColors.light : flowTypeBadgeColors.dark,
                      borderRadius: 8,
                    }}>
                      <MaterialCommunityIcons
                        name="sitemap"
                        size={12}
                        color={theme ? flowTypeBadgeColors.textLight : flowTypeBadgeColors.textDark}
                      />
                      <Text style={{
                        color: theme ? flowTypeBadgeColors.textLight : flowTypeBadgeColors.textDark,
                        fontSize: 11,
                        fontWeight: '600',
                        textTransform: 'capitalize'
                      }}>
                        {flowDataState.flowType}
                      </Text>
                    </View>

                    {/* Voice Enabled Badge */}
                    {flowDataState.voiceCallConfig?.enabled && (
                      <View style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 4,
                        paddingHorizontal: 10,
                        paddingVertical: 4,
                        backgroundColor: theme ? '#e0e7ff' : '#312e81',
                        borderRadius: 8,
                      }}>
                        <MaterialIcons
                          name="phone-in-talk"
                          size={12}
                          color={theme ? '#4f46e5' : '#a5b4fc'}
                        />
                        <Text style={{
                          color: theme ? '#4f46e5' : '#a5b4fc',
                          fontSize: 11,
                          fontWeight: '600'
                        }}>
                          Voice
                        </Text>
                      </View>
                    )}
                  </XStack>

                  <Text style={{
                    fontSize: 24,
                    fontWeight: '700',
                    color: theme ? "#1e293b" : "#f1f5f9",
                    marginBottom: 4
                  }}>
                    {flowDataState.title}
                  </Text>
                  <Text style={{
                    fontSize: 14,
                    color: theme ? "#64748b" : "#94a3b8"
                  }}>
                    {flowDataState.description}
                  </Text>
                </YStack>

                <XStack alignItems="center" gap={8}>
                  {/* Mode Badge */}
                  <View style={{
                    backgroundColor: isEditMode
                      ? (theme ? "#ddd6fe" : "#312e81")
                      : (theme ? "#dbeafe" : "#1e40af"),
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderRadius: 20,
                  }}>
                    <Text style={{
                      fontSize: 12,
                      fontWeight: '600',
                      color: isEditMode
                        ? (theme ? "#7c3aed" : "#a78bfa")
                        : (theme ? "#1e40af" : "#93c5fd")
                    }}>
                      {isEditMode ? 'EDIT MODE' : 'VIEW MODE'}
                    </Text>
                  </View>

                  {/* Collapse Button */}
                  <Pressable
                    onPress={() => setIsHeaderCollapsed(true)}
                    style={{
                      padding: 8,
                      borderRadius: 8,
                      backgroundColor: theme ? "#f1f5f9" : "#334155",
                    }}
                  >
                    <MaterialCommunityIcons
                      name="chevron-up"
                      size={20}
                      color={theme ? "#64748b" : "#94a3b8"}
                    />
                  </Pressable>
                </XStack>
              </XStack>

              {/* Action Buttons */}
              <XStack gap={12} flexWrap="wrap">
                {/* Back Button - First in row */}
                <Pressable
                  onPress={() => navigation.goBack()}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: theme ? "#f1f5f9" : "#334155",
                    paddingHorizontal: 16,
                    paddingVertical: 10,
                    borderRadius: 8,
                    gap: 8
                  }}
                >
                  <MaterialIcons
                    name="arrow-back"
                    size={16}
                    color={theme ? "#64748b" : "#94a3b8"}
                  />
                  <Text style={{
                    fontSize: 14,
                    fontWeight: '600',
                    color: theme ? "#64748b" : "#94a3b8"
                  }}>
                    Back
                  </Text>
                </Pressable>

                  {/* Refresh Button - Available in both modes */}
                  <Pressable
                    onPress={() => refetchFlows()}
                    disabled={isLoadingFlows}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      backgroundColor: theme ? "#e0f2fe" : "#0c4a6e",
                      paddingHorizontal: 16,
                      paddingVertical: 10,
                      borderRadius: 8,
                      gap: 8,
                      opacity: isLoadingFlows ? 0.6 : 1
                    }}
                  >
                    <MaterialIcons
                      name="refresh"
                      size={16}
                      color={theme ? "#0284c7" : "#7dd3fc"}
                    />
                    <Text style={{
                      fontSize: 14,
                      fontWeight: '600',
                      color: theme ? "#0284c7" : "#7dd3fc"
                    }}>
                      {isLoadingFlows ? 'Loading...' : 'Refresh'}
                    </Text>
                  </Pressable>

                {/* Test Flow Button - Available in both modes */}
                <Pressable
                  onPress={handleTestFlow}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: theme ? "#dcfce7" : "#14532d",
                    paddingHorizontal: 16,
                    paddingVertical: 10,
                    borderRadius: 8,
                    gap: 8
                  }}
                >
                  <MaterialCommunityIcons
                    name="play-circle"
                    size={16}
                    color={theme ? "#16a34a" : "#86efac"}
                  />
                  <Text style={{
                    fontSize: 14,
                    fontWeight: '600',
                    color: theme ? "#16a34a" : "#86efac"
                  }}>
                    Test Flow
                  </Text>
                </Pressable>

                {isEditMode && (
                  <>
                    <Pressable
                      onPress={handleAddStage}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        backgroundColor: theme ? "#dbeafe" : "#1e40af",
                        paddingHorizontal: 16,
                        paddingVertical: 10,
                        borderRadius: 8,
                        gap: 8
                      }}
                    >
                      <MaterialIcons
                        name="add"
                        size={16}
                        color={theme ? "#1e40af" : "#93c5fd"}
                      />
                      <Text style={{
                        fontSize: 14,
                        fontWeight: '600',
                        color: theme ? "#1e40af" : "#93c5fd"
                      }}>
                        Add Stage
                      </Text>
                    </Pressable>

                    <Pressable
                      onPress={handleSaveFlow}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        backgroundColor: theme ? "#d1fae5" : "#064e3b",
                        paddingHorizontal: 16,
                        paddingVertical: 10,
                        borderRadius: 8,
                        gap: 8
                      }}
                    >
                      <MaterialIcons
                        name="save"
                        size={16}
                        color={theme ? "#059669" : "#10b981"}
                      />
                      <Text style={{
                        fontSize: 14,
                        fontWeight: '600',
                        color: theme ? "#059669" : "#10b981"
                      }}>
                        Save Flow
                      </Text>
                    </Pressable>

                    <Pressable
                      onPress={handlePublishFlow}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        backgroundColor: theme ? "#fef3c7" : "#78350f",
                        paddingHorizontal: 16,
                        paddingVertical: 10,
                        borderRadius: 8,
                        gap: 8
                      }}
                    >
                      <MaterialIcons
                        name="publish"
                        size={16}
                        color={theme ? "#d97706" : "#f59e0b"}
                      />
                      <Text style={{
                        fontSize: 14,
                        fontWeight: '600',
                        color: theme ? "#d97706" : "#f59e0b"
                      }}>
                        Publish
                      </Text>
                    </Pressable>
                  </>
                )}
              </XStack>

              {/* Flow Stats */}
              <XStack gap={20} marginTop={16} flexWrap="wrap">
                <XStack alignItems="center" gap={6}>
                  <MaterialCommunityIcons
                    name="stairs"
                    size={16}
                    color={theme ? "#3b82f6" : "#60a5fa"}
                  />
                  <Text style={{
                    fontSize: 12,
                    color: theme ? "#64748b" : "#94a3b8"
                  }}>
                    {stages.length} stages
                  </Text>
                </XStack>

                <XStack alignItems="center" gap={6}>
                  <MaterialIcons
                    name="access-time"
                    size={16}
                    color={theme ? "#059669" : "#10b981"}
                  />
                  <Text style={{
                    fontSize: 12,
                    color: theme ? "#64748b" : "#94a3b8"
                  }}>
                    {totalEstimatedDuration} min total
                  </Text>
                </XStack>

                <XStack alignItems="center" gap={6}>
                  <MaterialCommunityIcons
                    name="chart-line"
                    size={16}
                    color={theme ? "#7c3aed" : "#a78bfa"}
                  />
                  <Text style={{
                    fontSize: 12,
                    color: theme ? "#64748b" : "#94a3b8"
                  }}>
                    {flowDataState.analytics?.completionRate?.toFixed(1) || 0}% completion
                  </Text>
                </XStack>

                <XStack alignItems="center" gap={6}>
                  <MaterialIcons
                    name="play-circle-outline"
                    size={16}
                    color={theme ? "#f59e0b" : "#fbbf24"}
                  />
                  <Text style={{
                    fontSize: 12,
                    color: theme ? "#64748b" : "#94a3b8"
                  }}>
                    {flowDataState.analytics?.totalExecutions || 0} runs
                  </Text>
                </XStack>
              </XStack>
            </>
          )}
        </YStack>
      </Card>
      <Separator borderColor={theme ? "#e2e8f0" : "#334155"} />
      {/* Hierarchy Editor */}
      <View style={{ flex: 1, alignSelf: 'stretch' }}>
        <FlowHierarchy
          ref={flowHierarchyRef}
          flowId={flowId || 'default-flow'}
          flowTitle={flowDataState?.title || 'Untitled Flow'}
          flowDescription={flowDataState?.description || ''}
          initialStages={Array.isArray(transformedStages) ? transformedStages : []}
          mode={mode || 'view'}
          onStagePositionChange={handleStagePositionChange}
          onStageConnectionChange={handleStageConnectionChange}
          onStageEdit={handleEditStage}
          onStageDelete={handleDeleteStage}
          onAddSubStage={handleAddSubStage}
        />
      </View>

      {/* Stage Editor - Full Screen Overlay */}
      {isEditorVisible && editingStage && (
        <StageEditor
          isVisible={isEditorVisible}
          stage={editingStage}
          onClose={handleCloseEditor}
          onSave={handleSaveStage}
          flowId={flowId || 'default-flow'}
        />
      )}
    </DefaultScreen>
  );
};

export default FlowEditor;

